import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import dayjs from "dayjs";
import {
  useGetFlowsQuery,
  useFlowOrderReportQuery,
  useSmsOrderReportQuery,
  useSmsflowreportcountsQuery,
} from "../store/services/flowApi";
import {
  useRcsflowreportcountQuery,
  useRcsflowreportcountsQuery,
} from "../store/services/rcstemplateApi";
import {
  useGetWhatsappFlowReportQuery,
  useGetwhatsappFlowreportCountQuery,
} from "../store/services/whatsappApi";
import { jwtDecode } from "jwt-decode";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import { toast } from "react-toastify";

const inputSx = {
  borderRadius: "0.5rem",
  minWidth: 140,
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.7)",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#94A3B8" },
  },
  "& .MuiInputLabel-root": { color: "#475569" },
  "& .Mui-disabled": {
    "& .MuiOutlinedInput-input": {
      color: "#334155 !important",
      WebkitTextFillColor: "#334155 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  },
};

const StatusDetailsDialog = ({
  open,
  onClose,
  campaignFlowId,
  status,
  type,
  customerId,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reset page whenever dialog opens / status / type changes
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, status, type, campaignFlowId]);

  const smsStatusParam =
    type === "sms" && (status === "" || status === "total") ? "sent" : status;

  const {
    data: smsDetails,
    isLoading: smsLoading,
    isFetching: smsFetching,
    isError: smsError,
  } = useSmsflowreportcountsQuery(
    {
      campaignflow_id: campaignFlowId,
      status: smsStatusParam,
      page,
      limit: rowsPerPage,
    },
    { skip: type !== "sms" || !open },
  );

  const {
    data: rcsDetails,
    isLoading: rcsLoading,
    isFetching: rcsFetching,
    isError: rcsError,
  } = useRcsflowreportcountsQuery(
    {
      campaignflow_id: campaignFlowId,
      status,
      page,
      limit: rowsPerPage,
    },
    { skip: type !== "rcs" || !open },
  );

  const whatsappStatusParam =
    type === "whatsapp" && status === "total" ? "" : status;

  const {
    data: whatsappDetails,
    isLoading: whatsappLoading,
    isError: whatsappError,
  } = useGetwhatsappFlowreportCountQuery(
    {
      customer_id: customerId,
      campaign_flow_id: campaignFlowId,
      status: whatsappStatusParam,
    },
    { skip: type !== "whatsapp" || !open || !customerId },
  );

  const isLoading =
    type === "sms"
      ? smsLoading
      : type === "rcs"
        ? rcsLoading
        : type === "whatsapp"
          ? whatsappLoading
          : false;

  const isFetching =
    type === "sms"
      ? smsFetching
      : type === "rcs"
        ? rcsFetching
        : false;

  const isError =
    type === "sms"
      ? smsError
      : type === "rcs"
        ? rcsError
        : type === "whatsapp"
          ? whatsappError
          : false;

  let detailsData = [];
  let pagination = null;

  if (type === "sms") {
    detailsData = smsDetails?.data || [];
    pagination = smsDetails?.pagination || null;
  } else if (type === "rcs") {
    detailsData = rcsDetails?.data || [];
    pagination = rcsDetails?.pagination || null;
  } else if (type === "whatsapp") {
    const campaigns = whatsappDetails?.data || [];
    campaigns.forEach((campaign) => {
      if (campaign.messages) {
        detailsData = [...detailsData, ...campaign.messages];
      }
    });
  }

  const totalPages = pagination?.totalPages || 1;
  const totalRecords = pagination?.totalRecords || detailsData.length;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm:ss") : value;
  };

  const statusCell = (item, statusValue) => {
    const isFailed = String(statusValue || "")
      .toLowerCase()
      .includes("fail");
    return (
      <>
        <Chip
          label={statusValue || "-"}
          size="small"
          color={isFailed ? "error" : "success"}
          variant="outlined"
          sx={{ height: 20, fontSize: "0.65rem" }}
        />
        {item.dnd_status && (
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ fontSize: "0.65rem" }}
          >
            {item.dnd_status}
          </Typography>
        )}
      </>
    );
  };

  const columnsByType = {
    sms: [
      { label: "Created At", render: (item) => formatDateTime(item.submit_date) },
      { label: "Phone Number", render: (item) => item.ph_no || "-" },
      { label: "Sender ID", render: (item) => item.sender_id || "-" },
      { label: "Status", render: (item) => statusCell(item, item.stat) },
      { label: "Status Updated Time", render: (item) => formatDateTime(item.done_date) },
    ],
    rcs: [
      {
        label: "Send Date",
        render: (item) =>
          formatDateTime(item.send_time || item.submit_date || item.created_at),
      },
      { label: "Done Time", render: (item) => formatDateTime(item.done_time || item.updated_at) },
      { label: "Mobile", render: (item) => item.user || item.ph_no || "-" },
      {
        label: "Status",
        render: (item) => statusCell(item, item.message_status || item.stat),
      },
      { label: "Provider", render: (item) => item.provider || "-" },
      {
        label: "Error Code",
        render: (item) => item.errcode || item.error_code || "-",
      },
      {
        label: "Error Description",
        render: (item) =>
          item.reason || item.errordescription || item.error_description || "-",
      },
    ],
  };

  const genericColumns = [
    {
      label: "Date/Time",
      render: (item) =>
        formatDateTime(
          item.submit_date || item.updated_at || item.created_at,
        ),
    },
    {
      label: "Mobile",
      render: (item) =>
        item.ph_no ||
        item.phone_number ||
        item.recipient ||
        item.message_data?.to ||
        "-",
    },
    {
      label: "Status",
      render: (item) =>
        statusCell(item, item.stat || item.status || item.message_data?.status),
    },
    {
      label: "Error Code",
      render: (item) =>
        item.error_code || item.err_code || item.message_data?.error_code || "-",
    },
    {
      label: "Error Description",
      render: (item) =>
        item.error_description ||
        item.err_desc ||
        item.message_data?.error_message ||
        "-",
    },
  ];

  const columns = columnsByType[type] || genericColumns;

  const dialogTitle =
    status === "" || status === "total"
      ? `Total Details (${type?.toUpperCase()})`
      : `${status} Details (${type?.toUpperCase()})`;

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const showPagination = type === "sms" || type === "rcs";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
          {dialogTitle}
          {totalRecords > 0 && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: "#64748B", fontWeight: "normal" }}
            >
              ({totalRecords} records)
            </Typography>
          )}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Top controls for pagination (rows per page) */}
        {showPagination && totalRecords > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Rows per page:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                sx={{
                  fontSize: "0.75rem",
                  height: "30.75px",
                  "& .MuiSelect-select": { padding: "6px 8px" },
                }}
              >
                {[10, 25, 50, 100].map((count) => (
                  <MenuItem
                    key={count}
                    value={count}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {count}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography color="error" align="center">
            Failed to load details.
          </Typography>
        ) : detailsData.length === 0 ? (
          <Typography align="center" color="text.secondary">
            No records found.
          </Typography>
        ) : (
          <Box sx={{ position: "relative" }}>
            {isFetching && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(255,255,255,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell
                        key={col.label}
                        sx={{
                          bgcolor: "#0288d1",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "0.75rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailsData.map((item, index) => (
                    <TableRow
                      key={item.sr_no ?? item.report_id ?? index}
                      hover
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.label}
                          sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                        >
                          {col.render(item)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {showPagination && totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FlowDetails = ({ campaignFlowId, type, customerId: flowCustomerId }) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const {
    data: smsData,
    isLoading: smsLoading,
    isError: smsError,
  } = useSmsOrderReportQuery(
    { campaignflow_id: campaignFlowId },
    { skip: type !== "sms" },
  );

  const {
    data: rcsData,
    isLoading: rcsLoading,
    isError: rcsError,
  } = useRcsflowreportcountQuery(campaignFlowId, { skip: type !== "rcs" });

  const token = localStorage.getItem("accessToken");
  let customerId = null;
  if (token) {
    try {
      const decoded = jwtDecode(token);
      customerId = decoded?.customer_id || decoded?.id || decoded?.userid;
    } catch (e) {
      console.error("Token decode error", e);
    }
  }

  const {
    data: whatsappData,
    isLoading: whatsappLoading,
    isError: whatsappError,
  } = useGetWhatsappFlowReportQuery(
    {
      customer_id: flowCustomerId || customerId,
      campaignflow_id: campaignFlowId,
    },
    { skip: type !== "whatsapp" || (!flowCustomerId && !customerId) },
  );

  const isLoading =
    type === "sms"
      ? smsLoading
      : type === "rcs"
        ? rcsLoading
        : type === "whatsapp"
          ? whatsappLoading
          : false;
  const isError =
    type === "sms"
      ? smsError
      : type === "rcs"
        ? rcsError
        : type === "whatsapp"
          ? whatsappError
          : false;
  const reportData =
    type === "sms"
      ? smsData
      : type === "rcs"
        ? rcsData
        : type === "whatsapp"
          ? whatsappData
          : null;

  if (isLoading)
    return (
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Loading details...</Typography>
      </Box>
    );
  if (isError)
    return (
      <Typography variant="caption" color="error" sx={{ p: 2 }}>
        Failed to load details.
      </Typography>
    );

  let details = reportData?.data || reportData || {};

  if (type === "whatsapp" && details?.status_summary) {
    details = details.status_summary;
  }

  if (type === "rcs" && details?.overview) {
    details = details.overview;
  }

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const normalizeStatusKey = (key) => {
    const normalized = String(key || "").trim().toLowerCase();
    if (normalized === "totalfailed" || normalized === "total-failed") {
      return "failed";
    }
    return normalized;
  };

  const getWhatsAppDisplayValue = (key, value, data) => {
    if (type !== "whatsapp") return value;

    const normalizedKey = String(key || "").trim().toLowerCase();
    const total = toNum(data?.total ?? 0);
    const sent = toNum(data?.sent ?? 0);
    const delivered = toNum(data?.delivered ?? 0);
    const read = toNum(data?.read ?? 0);
    const failed = toNum(data?.failed ?? 0);

    switch (normalizedKey) {
      case "total":
        return total;
      case "sent":
        return sent;
      case "delivered":
        return delivered;
      case "read":
        return read;
      case "failed":
        return failed;
      default:
        return value;
    }
  };

  const handleChipClick = (key) => {
    const normalizedKey = String(key || "").trim().toLowerCase();
    if (normalizedKey === "total") {
      setSelectedStatus(type === "sms" ? "sent" : "total");
      setOpenDialog(true);
      return;
    }

    if (normalizedKey === "submit" && type === "rcs") {
      setSelectedStatus("sent");
      setOpenDialog(true);
      return;
    }

    setSelectedStatus(normalizeStatusKey(key));
    setOpenDialog(true);
  };

  // -------------------- SMS ORDER --------------------
  const SMS_ALLOWED_PREFIXES = [
    "total",
    "accept",
    "sent",
    "delivrd",
    "read",
    "failed",
    "pending",
  ];
  const isSmsAllowedKey = (lowerKey) =>
    SMS_ALLOWED_PREFIXES.some((prefix) => lowerKey.includes(prefix));

  const getSmsStatusOrder = (key) => {
    const lowerKey = String(key || "").toLowerCase();
    for (let i = 0; i < SMS_ALLOWED_PREFIXES.length; i++) {
      if (lowerKey === SMS_ALLOWED_PREFIXES[i]) return i;
    }
    for (let i = 0; i < SMS_ALLOWED_PREFIXES.length; i++) {
      if (lowerKey.includes(SMS_ALLOWED_PREFIXES[i])) return i;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  // -------------------- RCS ORDER --------------------
  const RCS_DISPLAY_KEYS = [
    "total",
    "sent",
    "delivered",
    "read",
    "failed",
    "pending",
  ];

  const RCS_EXCLUDED_KEYS = [
    "submit",
    "rejected",
    "disabled",
    "limitexhausted",
    "ttlexpired",
    "otherfailed",
  ];

  const normalizeRcsKey = (key) => {
    const lower = String(key || "").trim().toLowerCase();
    if (
      lower === "totalfailed" ||
      lower === "total-failed" ||
      lower === "total_failed" ||
      lower === "failed"
    ) {
      return "failed";
    }
    if (lower === "delivrd" || lower === "deliver") return "delivered";
    return lower;
  };

  const getRcsStatusOrder = (key) => {
    const normalized = normalizeRcsKey(key);
    const index = RCS_DISPLAY_KEYS.indexOf(normalized);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  // -------------------- WHATSAPP ORDER --------------------
  const WHATSAPP_DISPLAY_KEYS = [
    "total",
    "accepted",
    "sent",
    "delivered",
    "read",
    "skipped",
    "failed",
    "pending",
  ];

  const getWhatsappStatusOrder = (key) => {
    const lower = String(key || "").trim().toLowerCase();
    const index = WHATSAPP_DISPLAY_KEYS.indexOf(lower);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  const isWhatsappAllowedKey = (lowerKey) =>
    WHATSAPP_DISPLAY_KEYS.includes(lowerKey);

  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
      {Object.entries(details)
        .filter(([key, value]) => {
          if (typeof value !== "number" && typeof value !== "string")
            return false;

          const lowerKey = key.toLowerCase();

          if (
            [
              "campaignflow_id",
              "id",
              "_id",
              "smstext",
              "template_name",
              "msg",
              "message",
            ].includes(lowerKey)
          )
            return false;

          if (type === "rcs") {
            const normalizedKey = normalizeRcsKey(lowerKey);
            if (RCS_EXCLUDED_KEYS.includes(normalizedKey)) return false;
            if (!RCS_DISPLAY_KEYS.includes(normalizedKey)) return false;
          }

          if (type === "sms" && !isSmsAllowedKey(lowerKey)) return false;

          if (type === "whatsapp" && !isWhatsappAllowedKey(lowerKey))
            return false;

          return true;
        })
        .sort(([a], [b]) => {
          if (type === "sms") {
            return getSmsStatusOrder(a) - getSmsStatusOrder(b);
          }
          if (type === "rcs") {
            return getRcsStatusOrder(a) - getRcsStatusOrder(b);
          }
          if (type === "whatsapp") {
            return getWhatsappStatusOrder(a) - getWhatsappStatusOrder(b);
          }
          return 0;
        })
        .map(([key, value]) => {
          const normalizedKey = normalizeRcsKey(key);
          const isRcsFailed = type === "rcs" && normalizedKey === "failed";

          let displayValue = value;
          let displayKey = key;

          if (isRcsFailed) {
            displayKey = "failed";
            const totalFailed = toNum(
              details.TotalFailed ??
                details.totalfailed ??
                details.total_failed ??
                details["total-failed"] ??
                0
            );
            const rejected = toNum(details.rejected ?? 0);
            displayValue = totalFailed + rejected;
          }

          return (
            <Chip
              key={key}
              label={`${displayKey}: ${displayValue}`}
              size="small"
              onClick={() => handleChipClick(normalizedKey)}
              sx={{
                bgcolor: displayKey.includes("fail")
                  ? "#ffebee"
                  : displayKey.includes("deliver")
                    ? "#e8f5e9"
                    : "#e3f2fd",
                color: displayKey.includes("fail")
                  ? "#c62828"
                  : displayKey.includes("deliver")
                    ? "#2e7d32"
                    : "#1565c0",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textTransform: "capitalize",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                  boxShadow: 1,
                },
              }}
            />
          );
        })}
      {Object.keys(details).length === 0 && (
        <Typography variant="caption" sx={{ color: "#777" }}>
          No details available
        </Typography>
      )}

      {openDialog && (
        <StatusDetailsDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          campaignFlowId={campaignFlowId}
          status={selectedStatus}
          type={type}
          customerId={flowCustomerId || customerId}
        />
      )}
    </Box>
  );
};

const FlowReport = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedInfo, setExpandedInfo] = useState({
    rowIndex: null,
    orderKey: null,
  });

  const { data: flowOrderReport, isLoading: flowOrderReportLoading } =
    useFlowOrderReportQuery({
      startDate: startDate ? startDate.format("YYYY-MM-DD") : undefined,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : undefined,
      page,
      limit: rowsPerPage,
    });

  const getMaxEndDate = () => {
    if (!startDate) return dayjs();
    const oneMonthLater = startDate.add(1, "month");
    return oneMonthLater.isBefore(dayjs()) ? oneMonthLater : dayjs();
  };
  const isError = false;

  const flows = useMemo(() => flowOrderReport?.data || [], [flowOrderReport]);
  const totalPages = flowOrderReport?.pagination?.totalPages || 1;

  const dynamicOrderKeys = useMemo(() => {
    const keys = new Set();
    flows.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key.startsWith("order") && !key.includes("_")) {
          keys.add(key);
        }
      });
    });
    return Array.from(keys).sort((a, b) => {
      const numA = parseInt(a.replace("order", ""), 10);
      const numB = parseInt(b.replace("order", ""), 10);
      return numA - numB;
    });
  }, [flows]);

  const filteredFlows = useMemo(() => {
    if (!searchTerm) return flows;
    return flows.filter(
      (flow) =>
        flow.flow_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        flow.campaign_flow_id?.toString().includes(searchTerm) ||
        flow.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [flows, searchTerm]);

  const paginatedFlows = filteredFlows;

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  const handleOrderClick = (rowIndex, orderKey) => {
    setExpandedInfo((prev) => {
      if (prev.rowIndex === rowIndex && prev.orderKey === orderKey) {
        return { rowIndex: null, orderKey: null };
      }
      return { rowIndex, orderKey };
    });
  };

  const headerColumns = useMemo(() => {
    return ["Date", "Campaign Flow ID", "Flow Name", ...dynamicOrderKeys];
  }, [dynamicOrderKeys]);

  return (
    <Box sx={{ ...layoutCSS, minHeight: "calc(100vh - 100px)" }}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: "bold", fontSize: "0.9rem" }}
      >
        Flow Report
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => {
              setStartDate(newValue);
              if (
                endDate &&
                newValue &&
                endDate.diff(newValue, "month", true) > 1
              ) {
                setEndDate(newValue.add(1, "month"));
              }
            }}
            maxDate={dayjs()}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => {
              if (
                startDate &&
                newValue &&
                newValue.diff(startDate, "month", true) > 1
              ) {
                toast.error("Date range cannot exceed 1 month");
                return;
              }
              setEndDate(newValue);
            }}
            minDate={startDate}
            maxDate={getMaxEndDate()}
            format="DD/MM/YYYY"
            sx={{
              ...inputSx,
              width: 140,
            }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>

        <TextField
          size="small"
          placeholder="Search Flow Name/ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={inputSx}
        />

        <FormControl size="small" sx={{ minWidth: 60 }}>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            displayEmpty
            sx={{
              fontSize: "0.75rem",
              height: "30.75px",
              "& .MuiSelect-select": { padding: "6px 8px" },
            }}
          >
            {[10, 25, 50, 100].map((count) => (
              <MenuItem
                key={count}
                value={count}
                sx={{ fontSize: "0.75rem" }}
              >
                {count}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {headerColumns.map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    bgcolor: "#0288d1",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    py: 1,
                    borderRight: "0.5px solid #1b1b1bff",
                    textTransform: "capitalize",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {flowOrderReportLoading ? (
              <TableRow>
                <TableCell
                  colSpan={headerColumns.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    Loading flows...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell
                  colSpan={headerColumns.length}
                  align="center"
                  sx={{ py: 3, color: "error.main" }}
                >
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    Failed to load flow report data.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedFlows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headerColumns.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    No data found for the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedFlows.map((flow, index) => (
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={cellSX}>
                      {flow.date ? (
                        <>
                          {dayjs(flow.date).format("DD/MM/YYYY")}
                          <br />
                          {dayjs(flow.date).format("HH:mm")}
                        </>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={cellSX}>{flow.campaignflow_id}</TableCell>
                    <TableCell sx={cellSX}>
                      {flow.campaign_name || "-"}
                    </TableCell>
                    {dynamicOrderKeys.map((key) => (
                      <TableCell
                        key={key}
                        sx={{
                          ...cellSX,
                          cursor: "pointer",
                          bgcolor:
                            expandedInfo.rowIndex ===
                              (page - 1) * rowsPerPage + index &&
                              expandedInfo.orderKey === key
                              ? "rgba(2, 136, 209, 0.08)"
                              : "inherit",
                          transition: "background-color 0.3s ease",
                        }}
                        onClick={() =>
                          handleOrderClick(
                            (page - 1) * rowsPerPage + index,
                            key,
                          )
                        }
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight:
                                expandedInfo.rowIndex ===
                                  (page - 1) * rowsPerPage + index &&
                                  expandedInfo.orderKey === key
                                  ? "bold"
                                  : "normal",
                            }}
                          >
                            {flow[key] || "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {expandedInfo.rowIndex ===
                    (page - 1) * rowsPerPage + index && (
                      <TableRow sx={{ bgcolor: "rgba(2, 136, 209, 0.04)" }}>
                        <TableCell
                          colSpan={headerColumns.length}
                          sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 3,
                              pl: 4,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.8rem",
                                color: "#555",
                              }}
                            >
                              {flow[expandedInfo.orderKey]} Details:
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1.5 }}>
                              <FlowDetails
                                campaignFlowId={flow.campaignflow_id}
                                type={flow[expandedInfo.orderKey]?.toLowerCase()}
                                customerId={flow.customer_id}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          size="small"
          showFirstButton
          showLastButton
        />
      </Box>
    </Box>
  );
};

export default FlowReport;